/*
 * Copyright (c) 2026 Bolmate B.V.
 *
 * Shared push state for the notifications page. Every notification card's
 * channels select needs the SAME data — the account's device tokens, this
 * browser's subscription endpoint, and the enable/disable actions of
 * usePushSubscription — so it is mounted ONCE here and consumed via context.
 * Mounting usePushSubscription per card would multiply its visibility
 * listeners and refresh cascades (5+ instances), and per-card device fetches
 * would hit listDeviceTokens once per card per refresh.
 */

import React, {createContext, forwardRef, ReactNode, useContext, useEffect, useImperativeHandle, useRef} from 'react';

import {getSetting, ResponseData} from '@dvrd/dvr-controls';

import {isPushPromptInFlight, usePushSubscription} from '../../hooks/usePushSubscription';

import {listDeviceTokens} from '../../util/requestSender';

import {PushChannelsHandle, PushSubscriptionState} from '../../util/interfaces';

import {PushDeviceLike} from '../../util/pushChannelUtil';

import {AppContext} from '../misc/appContext';

export interface PushChannelDevice extends PushDeviceLike {
    created_at?: string | null;
    last_seen_at?: string | null;
}

export interface PushChannelsValue {
    devices: Array<PushChannelDevice>;
    currentEndpoint: string | null;
    devicesLoading: boolean;
    state: PushSubscriptionState;
    enable: () => Promise<'device-limit' | void>;
    disable: () => Promise<void>;
    refresh: () => Promise<void>;
}

export const PushChannelsContext = createContext<PushChannelsValue | null>(null);

interface Props {
    children: ReactNode;
}

const PushChannelsProvider = forwardRef<PushChannelsHandle, Props>(function PushChannelsProvider(props, ref) {
    const context = useContext(AppContext);
    const userId = context.userContext.user?.id ?? null;
    const accountId = context.accountContext.account?.id ?? null;
    const enableWebPush = getSetting('enableWebPush') === true;
    // The page's ONE usePushSubscription instance (auto-resubscribe, phantom
    // cleanup, permission tracking). Card and selects consume it from here.
    const {state, enable, disable, refresh: refreshSubscription} = usePushSubscription(userId, accountId);

    const [devices, setDevices] = React.useState<Array<PushChannelDevice>>([]);
    const [devicesLoading, setDevicesLoading] = React.useState(true);
    const [currentEndpoint, setCurrentEndpoint] = React.useState<string | null>(null);
    // Monotonic id of the newest load; a stale response (slow request finishing
    // after an account switch) compares against it and drops its result.
    const loadSeqRef = useRef(0);
    // Latest account id, for the refresh() guard below.
    const accountIdRef = useRef(accountId);
    accountIdRef.current = accountId;

    function loadDevices() {
        const seq = ++loadSeqRef.current;
        if (!enableWebPush || !accountId) {
            setDevices([]);
            setDevicesLoading(false);
            return;
        }
        setDevicesLoading(true);
        listDeviceTokens({
            account_id: accountId,
            callback: (data: ResponseData) => {
                if (seq !== loadSeqRef.current) return;
                setDevicesLoading(false);
                if (!data.success) return;
                setDevices(data.tokens || data.device_tokens || []);
            },
            // Network failure / non-2xx never fires `callback` — drop the
            // loading flag instead of leaving the selects "loading" forever.
            errorCallback: () => {
                if (seq !== loadSeqRef.current) return;
                setDevicesLoading(false);
            }
        });
        if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
            // getRegistration (not .ready) — .ready can hang indefinitely on
            // iOS standalone PWAs even when a usable registration exists.
            navigator.serviceWorker
                .getRegistration('/')
                .then(reg => reg?.pushManager.getSubscription() ?? null)
                .then(sub => setCurrentEndpoint(sub?.endpoint ?? null))
                .catch(() => setCurrentEndpoint(null));
        }
    }

    async function refresh() {
        // Subscription refresh FIRST: it performs silent backend repairs
        // (re-registering a missing token, phantom cleanup). Loading the
        // device list before it would snapshot the pre-repair state and the
        // repaired row would only show after the NEXT refresh.
        const requestedAccountId = accountId;
        await refreshSubscription();
        // Bail if the account switched DURING the await: loadDevices() reads
        // accountId from this stale closure, and since it bumps loadSeqRef last
        // its old-account response would win over the new account's load.
        if (requestedAccountId !== accountIdRef.current) return;
        loadDevices();
    }

    useImperativeHandle(ref, () => ({refresh}), [userId, accountId]);

    useEffect(() => {
        setDevices([]);
        loadDevices();
    }, [userId, accountId]);

    // Re-sync the device list when the user returns to the tab — another
    // browser/tab may have added or removed a device. Keeps the account's
    // device set consistent across sessions (backend = source of truth) without
    // polling. Cheap: a single listDeviceTokens, deduped by loadSeqRef.
    useEffect(() => {
        // visibilitychange ONLY — deliberately NO 'focus' listener. Returning
        // to the tab fires focus AND visibilitychange back-to-back, which
        // doubled every device fetch. Skip while a permission prompt is in
        // flight: the OS dialog toggles visibility and refreshing then competes
        // with it for the main thread. Mirrors notificationsPage.tsx.
        const resync = () => {
            if (document.visibilityState === 'visible' && !isPushPromptInFlight()) loadDevices();
        };
        document.addEventListener('visibilitychange', resync);
        return () => document.removeEventListener('visibilitychange', resync);
    }, [userId, accountId]);

    return (
        <PushChannelsContext.Provider
            value={{devices, currentEndpoint, devicesLoading, state, enable, disable, refresh}}
        >
            {props.children}
        </PushChannelsContext.Provider>
    );
});

PushChannelsProvider.displayName = 'PushChannelsProvider';

export default PushChannelsProvider;
